import {
    ClickAwayListener,
    Grid,
    IconButton,
    Popper,
    Tooltip,
} from "@mui/material";
import { Cancel } from "@mui/icons-material";
import DateRangeIcon from "@mui/icons-material/DateRange";
import MenuIcon from "@mui/icons-material/Menu";
import AcxInputLabel from "components/UI/AcxInputLabel";
import AcxMainTextField from "components/UI/AcxMainTextField";
import _, { startCase } from "lodash";
import moment, { Moment } from "moment";
import React, { CSSProperties } from "react";
import {
    DateRangeLabel,
    DatePickerComponentStore,
    DateReferenceOption,
    InteractionDateReferenceOption,
    getDateFromDateRangeLabel,
} from "stores/ComponentStores/DatePickerComponentStore";
import { v4 as uuidv4 } from "uuid";
import hexToRGB from "utils/hexToRGB";
import Calendar from "../Calendar";
import AcxDateTypeSelector from "./AcxDateTypeSelector";
import { observer } from "mobx-react";
import { useTheme } from "@mui/material";
import { sxClsx } from "utils/styleClsx";

// Keep the popovers inside the viewport. A popover that hangs below the fold makes the
// document scrollable, and scrolling it slides the whole app shell out of view.
// "fixed" keeps the popover out of the document's scroll height; flip and
// preventOverflow (on both axes) move it so it stays fully visible at any zoom.
const viewportPopperOptions = { strategy: "fixed" as const };
const viewportPopperModifiers = [
    { name: "offset", options: { offset: [0, 8] } },
    { name: "flip", options: { padding: 8 } },
    {
        name: "preventOverflow",
        options: { altAxis: true, tether: false, padding: 8 },
    },
];

const defaultDateIntervalOptions: DateRangeLabel[] = [
    "Last 7 Days",
    "Last 30 Days",
    "This Week",
    "Last Week",
    "This Month",
    "Last Month",
    "This Quarter",
    "Last Quarter",
];

interface IAcxDateRangeInput {
    onSelect?: (
        selectedStartDate: moment.Moment,
        selectedEndDate: moment.Moment,
    ) => void;
    onDateTypeChange?(dateType: string): void;
    onDateRangeLabelChange?: (dateRangeLabel: DateRangeLabel) => void;
    onBlur?: () => void;
    onFocus?: () => void;
    defaultStartDate?: moment.Moment;
    defaultEndDate?: moment.Moment;
    labelText?: string;
    required?: boolean;
    wrapperStyle?: CSSProperties;
    maxWidth?: string;
    displayDateType?: boolean;
    dateReferenceOptions?:
        | DateReferenceOption[]
        | InteractionDateReferenceOption[];
    datePickerStore?: DatePickerComponentStore;
    inputStyle?: CSSProperties;
    innerStyles?: CSSProperties;
    hideLabel?: boolean;
    sideBar?: boolean;
    doubleCalendar?: boolean;
    viewOnly?: boolean;
    dateRangeLabel?: DateRangeLabel;
    /**
     * currently only used to attach custom ids for pendo for agent coaching domain date reference selection
     */
    useCustomDateTypeIds?: boolean;
    /**
     * currently only used to adjust the date range label options for the agents domain
     */
    dateIntervalOptions?: DateRangeLabel[];
    showClearButton?: boolean;
    onClear?: () => void;
    allowManualDateSelectionWithLabel?: boolean;
    /**
     * Calls onSelect only once both ends of the range are chosen: after the end date is
     * picked, or after focus leaves the typed inputs. Abandoning a half-picked calendar
     * range reverts to the defaults, so callers never receive a new start with an old end.
     */
    selectOnComplete?: boolean;
}

export default observer(function AcxDateRangeInput(props: IAcxDateRangeInput) {
    const theme = useTheme();
    const containerRef = React.useRef<HTMLDivElement>(null);

    const [openCalendarPopover, setOpenCalendarPopover] = React.useState(false);
    const [openDateTypePopover, setOpenDateTypePopover] = React.useState(false);

    const [warning, setWarning] = React.useState(false);

    const [selectedStartDate, setSelectedStartDate] = React.useState<
        Moment | undefined
    >(props.defaultStartDate ?? moment().subtract(30, "days"));

    const [selectedEndDate, setSelectedEndDate] = React.useState<
        Moment | undefined
    >(props.defaultEndDate ?? moment());

    const [choosingBegin, setChoosingBegin] = React.useState(false);

    const [dateChange, setDateChanged] = React.useState(false); //used to figure out what the user did with the range calendar

    const [shouldOnSelect, setShouldOnSelect] = React.useState(false);

    const [hasFocus, setHasFocus] = React.useState(false);
    // selectOnComplete: a typed date waits until focus leaves both inputs.
    const typedDatePending = React.useRef(false);
    const [endFocus, setEndFocus] = React.useState(false);
    const [beginFocus, setBeginFocus] = React.useState(false);

    const onDateClick = (dt: moment.Moment) => {
        if (choosingBegin) {
            if (dt.valueOf() !== selectedStartDate?.valueOf()) {
                setDateChanged(true);
            }

            if (dt.isAfter(selectedEndDate)) {
                setSelectedEndDate(dt);
            }

            setSelectedStartDate(dt);
            setChoosingBegin(false);
            if (props.selectOnComplete) return;
        } else {
            if (dt.isBefore(selectedStartDate)) {
                setSelectedEndDate(selectedStartDate);
                setSelectedStartDate(dt);
            } else {
                setSelectedEndDate(dt);
            }
            setOpenCalendarPopover(false);
            if (props.selectOnComplete) setDateChanged(false);
        }
        setShouldOnSelect(true);
    };

    const onCalendarClick = () => {
        setChoosingBegin(true);
        setOpenCalendarPopover(!openCalendarPopover);
    };

    const onDateTypeClick = () => {
        setOpenDateTypePopover(!openDateTypePopover);
    };

    const handleClickAway = () => {
        setChoosingBegin(false);
        setOpenCalendarPopover(false);
        setOpenDateTypePopover(false);

        if (dateChange) {
            setDateChanged(false);
            if (props.selectOnComplete) {
                // Only the start was picked; drop it rather than report a half range.
                setSelectedStartDate(props.defaultStartDate);
                setSelectedEndDate(props.defaultEndDate);
            } else {
                setShouldOnSelect(true);
            }
        }
    };

    const onSelect = React.useCallback(() => {
        typedDatePending.current = false;
        if (selectedStartDate && selectedEndDate) {
            // Typed dates can arrive reversed; order them as calendar clicks do.
            const reversed =
                props.selectOnComplete &&
                selectedStartDate.isAfter(selectedEndDate);
            props.onSelect?.(
                (reversed ? selectedEndDate : selectedStartDate).clone(),
                (reversed ? selectedStartDate : selectedEndDate).clone(),
            );
        }
    }, [props, selectedEndDate, selectedStartDate]);

    const onSelectRef = React.useRef(onSelect);
    React.useEffect(() => {
        onSelectRef.current = onSelect;
    }, [onSelect]);

    // Keep one timer across rerenders so clearing can cancel every pending selection.
    const debouncedOnSelect = React.useMemo(
        () => _.debounce(() => onSelectRef.current(), 500),
        [],
    );

    React.useEffect(() => () => debouncedOnSelect.cancel(), [debouncedOnSelect]);

    const debouncedSetWarning = React.useMemo(
        () => _.debounce((val: boolean) => setWarning(val), 500),
        [],
    );

    React.useEffect(() => {
        if (shouldOnSelect) {
            debouncedOnSelect();
            setShouldOnSelect(false);
        }
    }, [selectedStartDate, selectedEndDate, debouncedOnSelect, shouldOnSelect]);

    React.useEffect(() => {
        setSelectedStartDate(props.defaultStartDate);
        setSelectedEndDate(props.defaultEndDate);
    }, [props.defaultStartDate, props.defaultEndDate]);

    const validDateString = (dt: string) => {
        const reg =
            /^([0-9]|[0-1][0-2])\/([1-9]|[0-2][0-9]|[3][0-1])\/(19|20)\d{2}$/;

        const fmtTest = reg.test(dt);
        return fmtTest;
    };

    const onStartDateChange = (el: React.ChangeEvent<HTMLInputElement>) => {
        const val = el.currentTarget.value;
        debouncedOnSelect.cancel();
        if (!validDateString(val)) {
            debouncedSetWarning(true);
        } else {
            if (props.selectOnComplete) typedDatePending.current = true;
            else setShouldOnSelect(true);
            setSelectedStartDate(moment(val));
            debouncedSetWarning.cancel();
            setWarning(false);
        }
    };

    const onEndDateChange = (el: React.ChangeEvent<HTMLInputElement>) => {
        const val = el.currentTarget.value;
        debouncedOnSelect.cancel();
        if (!validDateString(val)) {
            debouncedSetWarning(true);
        } else {
            if (props.selectOnComplete) typedDatePending.current = true;
            else setShouldOnSelect(true);
            setSelectedEndDate(moment(val));
            // setRenderEndDate(moment(val));
            debouncedSetWarning.cancel();
            setWarning(false);
        }
    };

    const onBlur = () => {
        setHasFocus(false);
        if (typedDatePending.current) debouncedOnSelect();
        props.onBlur?.();
    };

    const onFocus = () => {
        setHasFocus(true);
        // Moving between the two inputs is still the same edit.
        if (typedDatePending.current) debouncedOnSelect.cancel();
        props.onFocus?.();
    };

    const handleSelectedDateOptionChange = (inputVal: DateRangeLabel) => {
        props.datePickerStore?.setDateRangePreset(inputVal);
        const [startDate, endDate] = getDateFromDateRangeLabel(inputVal);
        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);
        setShouldOnSelect(true);
        setOpenCalendarPopover(false);
        props.onDateRangeLabelChange?.(inputVal);
    };

    const hasDateRangeLabel = !!props.dateRangeLabel;
    const shouldLockToPresetSelection =
        hasDateRangeLabel && !props.allowManualDateSelectionWithLabel;
    const showClearButton = props.showClearButton ?? hasDateRangeLabel;

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <div style={props?.wrapperStyle ?? { maxWidth: "300px" }}>
                <Grid container>
                    <Grid item style={props?.inputStyle ?? {}}>
                        {!props.hideLabel && (
                            <AcxInputLabel
                                id={`date-range-label-${uuidv4()}`}
                                labelText={
                                    props.labelText ??
                                    (props.displayDateType
                                        ? `${startCase(
                                              props.datePickerStore
                                                  ?.referenceOption,
                                          )} Range`
                                        : "")
                                }
                                focused={hasFocus}
                                sx={sxClsx(
                                    {
                                        fontFamily: theme.typography.fontFamily,
                                        color: theme.palette.text.primary,
                                        fontSize: "13px",
                                        lineHeight: "16px",
                                    },
                                    hasFocus && {
                                        color: theme.palette.primary.main,
                                        fontSize: "13px",
                                        fontWeight:
                                            theme.typography.fontWeightSemibold,
                                    },
                                    props.required &&
                                        !selectedStartDate &&
                                        !selectedEndDate && {
                                            "&:after": {
                                                color: theme.palette.red.main,
                                                content: `" *"`,
                                            },
                                        },
                                )}
                            />
                        )}

                        <Grid
                            container
                            item
                            xs={12}
                            sx={sxClsx(
                                {
                                    border: "1px solid",
                                    borderColor:
                                        theme.palette.lightgrayBorder.main,
                                    backgroundColor: theme.palette.white.main,
                                    borderRadius: "3px",
                                    height: "32px",
                                    maxWidth: props.maxWidth ?? "350px",
                                    minWidth: props.wrapperStyle
                                        ? "100%"
                                        : "220px",
                                },
                                warning && {
                                    borderColor: theme.palette.error.main,
                                },
                                hasFocus &&
                                    !warning && {
                                        borderColor:
                                            theme.palette.lightgrayBorder.main,
                                    },
                            )}
                            ref={containerRef}
                            style={props.innerStyles ?? {}}
                            justifyContent={
                                hasDateRangeLabel ? "space-between" : undefined
                            }
                            alignItems={"center"}
                        >
                            {!props.viewOnly && (
                                <Grid container item xs={1}>
                                    <Tooltip
                                        title="Select calendar input"
                                        placement="top"
                                        arrow
                                    >
                                        <IconButton
                                            size="small"
                                            sx={sxClsx(
                                                {
                                                    color: hexToRGB(
                                                        theme.palette.black
                                                            .main,
                                                        0.4,
                                                    ),
                                                },
                                                warning && {
                                                    color: hexToRGB(
                                                        theme.palette.error
                                                            .main,
                                                        0.8,
                                                    ),
                                                },
                                                hasFocus &&
                                                    !warning && {
                                                        color: theme.palette
                                                            .primary.main,
                                                    },
                                            )}
                                            onClick={onCalendarClick}
                                        >
                                            <DateRangeIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            )}

                            {hasDateRangeLabel && (
                                <Grid
                                    container
                                    item
                                    xs={props.displayDateType ? 3 : 4}
                                    alignItems={"center"}
                                    fontWeight={600}
                                    onClick={
                                        props.viewOnly
                                            ? undefined
                                            : onCalendarClick
                                    }
                                    style={
                                        props.viewOnly
                                            ? { opacity: 0.75 }
                                            : { cursor: "pointer" }
                                    }
                                    color={
                                        props.viewOnly
                                            ? theme.palette.gray.main
                                            : undefined
                                    }
                                >
                                    <Grid
                                        container
                                        item
                                        justifyContent={"center"}
                                    >
                                        {props.dateRangeLabel}:
                                    </Grid>
                                </Grid>
                            )}

                            <Grid
                                container
                                item
                                xs={
                                    props.datePickerStore &&
                                    props.displayDateType
                                        ? hasDateRangeLabel
                                            ? showClearButton
                                                ? 5
                                                : 6
                                            : showClearButton
                                            ? 9
                                            : 10
                                        : hasDateRangeLabel
                                        ? showClearButton
                                            ? 6
                                            : 7
                                        : showClearButton
                                        ? 10
                                        : 11
                                }
                                justifyContent={
                                    hasDateRangeLabel ? "flex-start" : "center"
                                }
                                alignItems={"center"}
                            >
                                <Grid item xs={5}>
                                    <AcxMainTextField
                                        onBlur={() => {
                                            onBlur();
                                            setBeginFocus(false);
                                        }}
                                        onFocus={onFocus}
                                        onChange={onStartDateChange}
                                        value={selectedStartDate?.format(
                                            "M/D/YYYY",
                                        )}
                                        placeholderText="begindate"
                                        id={`date-input-begin-${uuidv4()}`}
                                        focus={beginFocus}
                                        onLeaveRight={() => setEndFocus(true)}
                                        textContainerSxOverride={{
                                            display: "flex",
                                            border: "none",
                                            height: "28px",
                                            alignItems: "center",
                                            textAlign: "center",
                                        }}
                                        centerAlignInputText
                                        disablePadding
                                        fullWidth
                                        isDisabled={
                                            props.viewOnly ||
                                            shouldLockToPresetSelection
                                        }
                                        containerId="calInput"
                                    />
                                </Grid>

                                <Grid
                                    container
                                    item
                                    xs={1}
                                    justifyContent={"center"}
                                    alignItems={"center"}
                                >
                                    -
                                </Grid>

                                <Grid item xs={5}>
                                    <AcxMainTextField
                                        onBlur={() => {
                                            onBlur();
                                            setEndFocus(false);
                                        }}
                                        onFocus={onFocus}
                                        onChange={onEndDateChange}
                                        value={selectedEndDate?.format(
                                            "M/D/YYYY",
                                        )}
                                        placeholderText="enddate"
                                        focus={endFocus}
                                        onLeaveLeft={() => {
                                            setEndFocus(false);
                                            setBeginFocus(true);
                                        }}
                                        id={`date-input-end-${uuidv4()}`}
                                        textContainerSxOverride={{
                                            display: "flex",
                                            border: "none",
                                            height: "28px",
                                            alignItems: "center",
                                            textAlign: "center",
                                        }}
                                        centerAlignInputText
                                        disablePadding
                                        fullWidth
                                        isDisabled={
                                            props.viewOnly ||
                                            shouldLockToPresetSelection
                                        }
                                        containerId="calInput"
                                    />
                                </Grid>
                            </Grid>
                            {showClearButton && (
                                <Grid container item xs={1}>
                                    <IconButton
                                        onClick={() => {
                                            debouncedOnSelect.cancel();
                                            setShouldOnSelect(false);
                                            setDateChanged(false);
                                            if (props.onClear) {
                                                props.onClear();
                                                return;
                                            }
                                            props.onDateRangeLabelChange?.("");
                                        }}
                                        size="small"
                                        disabled={props.viewOnly}
                                    >
                                        <Cancel
                                            fontSize="inherit"
                                            htmlColor={theme.palette.gray[300]}
                                        />
                                    </IconButton>
                                </Grid>
                            )}
                            {props.datePickerStore && props.displayDateType && (
                                <Grid
                                    container
                                    item
                                    xs={1}
                                    style={{
                                        justifyContent: "flex-end",
                                    }}
                                >
                                    <Tooltip
                                        placement="top"
                                        title="Select date type"
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={onDateTypeClick}
                                            sx={{
                                                color: hexToRGB(
                                                    theme.palette.black.main,
                                                    0.4,
                                                ),
                                            }}
                                        >
                                            <MenuIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>

                {warning && (
                    <AcxInputLabel
                        id={`AcxDateRangeInput-BottomLabel-${uuidv4()}`}
                        sx={sxClsx(
                            {
                                fontFamily: theme.typography.fontFamily,
                                color: theme.palette.text.primary,
                                fontSize: "12px",
                                lineHeight: "16px",
                            },
                            warning && { color: theme.palette.error.main },
                        )}
                    >
                        {"Please enter a date in M/D/YYYY format"}
                    </AcxInputLabel>
                )}

                <Popper
                    anchorEl={containerRef.current}
                    open={openCalendarPopover}
                    disablePortal={false}
                    sx={{ zIndex: 9001 }}
                    placement="bottom-start"
                    popperOptions={viewportPopperOptions}
                    modifiers={viewportPopperModifiers}
                >
                    <div
                        style={{
                            backgroundColor: theme.palette.white.main,
                            padding: theme.spacing(2),
                            borderRadius: "8px",
                            border: "0.5px solid var(--neutral-200, #D1D5DB)",
                            boxShadow:
                                "0px 8px 16px 0px rgba(23, 37, 76, 0.12)",
                        }}
                    >
                        <Calendar
                            onSelection={onDateClick}
                            selectedStartDate={selectedStartDate}
                            selectedEndDate={selectedEndDate}
                            sideBar={props.sideBar}
                            handleSelectedDateOptionChange={
                                handleSelectedDateOptionChange
                            }
                            doubleCalendar={props.doubleCalendar}
                            choosingBegin={choosingBegin}
                            isRangeSelect
                            showSideBarOnly={shouldLockToPresetSelection}
                            dateIntervalOptions={
                                props.dateIntervalOptions ??
                                defaultDateIntervalOptions
                            }
                        />
                    </div>
                </Popper>
                <Popper
                    anchorEl={containerRef.current}
                    open={openDateTypePopover}
                    disablePortal={false}
                    sx={{ zIndex: 9001 }}
                    placement="bottom-end"
                    popperOptions={viewportPopperOptions}
                    modifiers={viewportPopperModifiers}
                >
                    <div
                        style={{
                            backgroundColor: theme.palette.white.main,
                            padding: theme.spacing(2),
                            borderRadius: "8px",
                            border: "0.5px solid var(--neutral-200, #D1D5DB)",
                            boxShadow:
                                "0px 8px 16px 0px rgba(23, 37, 76, 0.12)",
                        }}
                    >
                        {props.datePickerStore && props.displayDateType && (
                            <Grid item sx={{ width: "150px" }}>
                                <AcxDateTypeSelector
                                    datePickerStore={props.datePickerStore}
                                    onChange={props.onDateTypeChange}
                                    dateReferenceOptions={
                                        props.dateReferenceOptions ?? []
                                    }
                                    useCustomIds={props.useCustomDateTypeIds}
                                />
                            </Grid>
                        )}
                    </div>
                </Popper>
            </div>
        </ClickAwayListener>
    );
});
